import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
// ============================================================================
// Execution Strategy — PROMPT BUILDER
// Produces a single opinionated execution strategy with prioritised actions,
// phasing, dependencies, risks, and explicit "do not do yet" guidance.
// ============================================================================

const pretty = (v) => JSON.stringify(v ?? null, null, 2);

export const buildAIExecutionStrategyPrompt = ({
  issueTypeStr,
  metricsHistory = [],
  transitionsJSON = [],
  variantsJSON = [],
  sampleItems = [],
  fieldUsage = [],
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityType = null,
  activityContext = null,
  reportScopeSummary = {},
  opportunityPortfolio = null,
}) => {
  const reportScopeText = buildReportScopePromptSection(reportScopeSummary);
  return `
You are an elite workflow transformation consultant.

Your job is NOT to produce another descriptive report.
Your job is to produce an opinionated EXECUTION STRATEGY for leadership.

The output must answer:
1. What should be done first?
2. What should be done second?
3. What should wait?
4. What should NOT be done yet?
5. How should the change be phased so it is realistic?

Be decisive. Avoid generic consulting filler.
Do not repeat the same insight in multiple sections.
Prefer a small number of high-value priorities over long lists.
Ground every recommendation in the supplied evidence.

ACTIVITY CONTEXT RULES
- The mined activity field may not be Status. Use ACTIVITY CONTEXT before interpreting paths, transitions, or activity values.
- If the activity is Assignment Group, treat transitions as routing/handoff/escalation movement between teams, not lifecycle status movement.
- If the activity is Assigned To, treat transitions as ownership/reassignment movement.
- Only use status/lifecycle language when ACTIVITY CONTEXT says lifecycle_status or the field is status/state.

CONTEXT
- ${issueTypeStr}
- The user already has separate tabs for process health, trend analysis, AI intelligence, automation packs, and consulting report.
- 
CONTROLLED ACTIVITY TYPE RULES
- The payload contains activity_context.analysis_type / activity_context.activity_kind. Treat this as the controlling interpretation, not merely a hint.
- Only use lifecycle/status evidence such as queue time, wait time, touch time, admin time, flow efficiency, status bottlenecks, or status progression when activity_kind is lifecycle_status.
- If activity_kind is routing_group, describe elapsed time as resolver-group dwell time, routing delay, escalation delay, handoff delay, or ownership dwell — not literal waiting in queues.
- If activity_kind is ownership, describe elapsed time as assignee dwell time, reassignment delay, ownership churn, or workload concentration — not status queue time.
- If activity_kind is classification, interpret transitions as classification changes, escalation/de-escalation, or data-quality movement. Do not describe this as workflow progress.
- If activity_kind is approval_state, interpret transitions as approval movement, approval dwell, rejection/rework, or governance delay.
- Respect activity_context.invalid_interpretations and activity_context.valid_metric_families when selecting evidence and wording.

This tab must be the missing DECISION / EXECUTION layer.
- It should feel like: "If you only do a few things this quarter, do these in this order."

STRICT OUTPUT RULES
- Return VALID JSON only.
- No markdown fences.
- No commentary outside JSON.
- Keep recommendations specific and implementation-aware.
- Priority actions should usually be between 3 and 5 items.
- Phase count should usually be 3.

RETURN THIS EXACT JSON SHAPE:
{
  "summary": {
    "headline": "",
    "executive_summary": "",
    "strategic_position": "Critical|High Priority|Targeted Improvement|Optimisation",
    "confidence": "High|Medium|Low",
    "why_now": ""
  },
  "priority_actions": [
    {
      "rank": 1,
      "title": "",
      "source_opportunity": "",
      "opportunity_type": "AI|Automation|Optimisation|",
      "recommended_priority": "Do Now|Plan|Investigate|Monitor|",
      "value_potential": "Very High|High|Medium|Low|",
      "priority_score": 0,
      "action_type": "Process|Automation|AI|Governance|Data",
      "why_now": "",
      "scope": "",
      "business_outcome": "",
      "impact_level": "Very High|High|Medium|Low",
      "effort_level": "High|Medium|Low",
      "time_horizon": "0-30 days|30-60 days|60-90 days|90+ days",
      "owner": "",
      "dependencies": [""],
      "risks": [""],
      "success_measures": [""],
      "evidence": [""]
    }
  ],
  "phased_plan": [
    {
      "phase": 1,
      "title": "",
      "objective": "",
      "why_this_phase": "",
      "included_priorities": [1],
      "entry_criteria": [""],
      "exit_criteria": [""],
      "expected_outcomes": [""]
    }
  ],
  "sequencing_principles": [
    {
      "title": "",
      "detail": ""
    }
  ],
  "do_not_do_yet": [
    {
      "item": "",
      "reason": ""
    }
  ]
}

${reportScopeText}
EVIDENCE
METRICS HISTORY
${pretty(metricsHistory)}

TOP TRANSITIONS
${pretty(transitionsJSON)}

TOP VARIANTS
${pretty(variantsJSON)}

SAMPLE WORK ITEMS
${pretty(sampleItems.slice(0, 20))}

FIELD USAGE
${pretty(fieldUsage)}

ACTIVITY CONTEXT
${pretty(activityContext || { activity_field: activityType, activity_label: activityType })}

ACTIVITY TYPE SUMMARY
${pretty(statusTypeSummary)}

ACTIVITY TYPE MAPPINGS
${pretty(statusTypeMappings)}

DECISION CRITERIA
- Prioritise actions that simplify flow, reduce major delay, reduce rework, improve standardisation, and create a stable base for automation/AI.
- If the workflow is chaotic, prioritise governance and process simplification before advanced AI.
- If data quality is strong, call out where AI/automation should come after simplification.
- Use evidence from metrics, transitions, variants, and field signals.
- Be honest about what should wait.

OPPORTUNITY PORTFOLIO LINKAGE
- If OPPORTUNITY PORTFOLIO is supplied below, treat it as the primary decision input for prioritisation. Do not invent a different opportunity list unless the evidence clearly contradicts it.
- Preserve the opportunity titles so the user can trace Opportunity Portfolio → Execution Strategy.
- Use recommended_priority, value_potential, priority_score/impact/effort/confidence, time_horizon and recommended_treatment when sequencing work.
- "Do Now" opportunities should normally appear first unless there is a blocking dependency.
- "Investigate" items should normally become validation actions rather than full implementation commitments.
- "Monitor" items should normally be deferred unless their risk profile justifies escalation.

OPPORTUNITY PORTFOLIO
${pretty(opportunityPortfolio)}
`;
};

export default buildAIExecutionStrategyPrompt;
